﻿import { useEffect } from 'react';

import { TableGeneric } from 'pages/reports/components/TableGeneric/TableGeneric';
import { Button, Col, Container, ListGroup, Row } from 'react-bootstrap';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { getReportIdFromPath } from 'utils/other';
import { ReportComponent } from 'apis/ReportApi';
import { useSuperState } from 'hooks';
import { ReportApi } from 'apis';

interface KaplanReportState {
    currentSubReport: any;
    loading: boolean;
    report33Data: any;
    report60Data: Record<string, any> | null;
}

interface KaplanReportProps {
    resultId?: number;
    studentProfileId?: number;
}

export const KaplanReport = ({ resultId: resultIdProp, studentProfileId }: KaplanReportProps = {}) => {

    const { formatMessage } = useIntl();
    const [searchParams] = useSearchParams();

    const [state, setState] = useSuperState<KaplanReportState>({
        currentSubReport: null,
        loading: true,
        report33Data: null,
        report60Data: null
    });

    const { currentSubReport, loading, report33Data, report60Data } = state;

    useEffect(() => {

        const urlStudentId = Number(searchParams.get('studentId'));
        const urlResultId = Number(searchParams.get('ScopeObjectId'));
        const studentId = urlStudentId || studentProfileId || 0;
        const resultId = urlResultId || resultIdProp || 0;

        ReportApi.GetKaplanResults(studentId, resultId)
            .then(response => {

                if (response) {

                    buildReport33FromResults(response);
                    buildReport60FromResults(response);

                }

            })
            .finally(() => {

                setState({ loading: false });

            });

    }, []);

    const buildReport33FromResults = (results: any[]) => {

        const report33 = {
            Component: {
                ComponentId: 1,
                ComponentName: 'Chart',
                CustomProperties: {
                    Value: results.map((r, i) => ({
                        FinishDate: r.FinishDate ?? '—',
                        ResultId: r.ResultId,
                        TestName: r.Name,
                        UniqueId: `${r.ResultId}_${i}`
                    }))
                },
                Section: 1,
                Type: 'ListOfActionItem'
            },
            Name: 'Test Attempt Report',
            ReportId: 33
        };

        setState({ report33Data: report33 });

        if (results.length > 0) {

            runSubReport(results[0]);

        }

    };

    const buildReport60FromResults = (response: any[]) => {

        const dataObj: Record<string, any> = {};

        response.forEach((item: any, i: number) => {

            const transformed = transformToReport60(item);

            // ResultIds are the same -- create unique key by combining ResultId with index
            const uniqueKey = `${item.ResultId}_${i}`;
            dataObj[uniqueKey] = transformed;
            transformed.UniqueId = uniqueKey;

        });

        const firstKey = `${response[0].ResultId}_0`;
        setState({ currentSubReport: dataObj[firstKey] || null, report60Data: dataObj });

    };

    const transformToReport60 = (result: any) => ({
        Components: [
            {
                ComponentId: 2,
                ComponentName: 'Test Details',
                Results: {
                    Data: [
                        {
                            CategoryName: 'Cybervista',
                            FinishDate: result.finishDate ? formatFinishDate(result.finishDate) : '--',
                            mode: result.CybervistaTestStatus,
                            PreviousScore: result.correct,
                            ProductName: 'Cybervista',
                            ResultId: result.ResultId,
                            Score: `${result.Score}/${result.QuestionCount}`,
                            TestName: result.Name,
                            TestType: result.CybervistaTestType
                        }
                    ]
                },
                Type: 'ListOfInfoBox'
            },
            {
                ComponentId: 3,
                ComponentName: 'Topic Analysis',
                CustomProperties: {},
                HeaderStyles: '18px',
                Results: {
                    Columns: [
                        { field: 'Topic' },
                        {
                            dataType: 'ProgressBar',
                            field: 'Pass Percentage'
                        }
                    ],
                    Data: buildTopicAnalysisData(result)
                },
                Type: 'Table'
            }
        ],
        Description: 'Sub Report for the test attempt report',
        Id: 60,
        Name: result.Name,
        UniqueId: ''
    });

    const formatFinishDate = (finishDate: any) => {

        const date = new Date(finishDate);

        return new Intl.DateTimeFormat('en-US', {
            day: '2-digit',
            hour: '2-digit',
            hour12: true,
            minute: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(date).replace(',', '');

    };

    const renderListOfActionItem = (component: any) => {

        const data: any[] = component.CustomProperties.Value.map((result: any) => ({
            Id: result.UniqueId,
            ResultData: result,
            Title: result.TestName
        }));

        if (!currentSubReport && data.length > 0) {

            setState({ currentSubReport: data[0] });
            runSubReport(data[0].ResultData);

        }

        return (
            <div className="chart-action-item bg-theme-item-secondary h-100 p-2 rounded-1">
                <div className="actionMobile overflow-y-auto">
                    {data.map((item, i) => (
                        <ListGroup key={`${currentSubReport?.UniqueId}-item-${i}`} variant="flush">
                            <ListGroup.Item
                                className={`actionItem rounded mb-1 text-strong border-0 d-flex align-items-center ${currentSubReport?.UniqueId === item.Id ? 'bg-theme' : 'bg-transparent'
                                    }`}
                                onClick={() => {

                                    setState({ currentSubReport: item });
                                    runSubReport(item.ResultData, i);

                                }}
                            >
                                {item.Title}
                            </ListGroup.Item>
                        </ListGroup>
                    ))}
                </div>
            </div>
        );

    };

    const renderComponents = (component: ReportComponent, index: number) => {

        const data = component.Results?.Data[0];

        if (component.Type === 'ListOfInfoBox') {

            component = {
                ...component,
                Results: {
                    ...component.Results,
                    SeriesList: [
                        {
                            Data: [
                                {
                                    Share: data.CategoryName,
                                    Title: formatMessage({ id: 'category.f324da36', defaultMessage: 'Category' })
                                },
                                {
                                    Share: data.ProductName,
                                    Title: formatMessage({ id: 'product.cf8158fa', defaultMessage: 'Product', description: 'Gridview header' })
                                },
                                {
                                    Share: data.TestType,
                                    Title: formatMessage({ id: 'test_type.30d1f5ef', defaultMessage: 'Test Type' })
                                },
                                {
                                    Share: data.mode,
                                    Title: formatMessage({ id: 'test_status.b0b80325', defaultMessage: 'Test Status' })
                                },
                                {
                                    Share: data.Score,
                                    Title: formatMessage({ id: 'score.6ed6415a', defaultMessage: 'Score' })
                                },
                                {
                                    Share: data.FinishDate,
                                    Title: formatMessage({ id: 'date_finished.70b7f1cf', defaultMessage: 'Date Finished' })
                                }
                            ]
                        }

                    ]
                },
                Title: formatMessage({ id: 'test_details.88f73a4e', defaultMessage: 'Test Details' })
            } as ReportComponent;

            return (
                <div className='bg-theme-item pb-3 rounded-0 print-only-column'>
                    {getReportIdFromPath() === '62' && (
                        <div className='d-flex justify-content-between align-items-center'>
                            <h3 className='mb-0'>
                                {component.Title}
                            </h3>
                            <Button
                                className="fw-bold"
                                onClick={window.print}
                                size='sm'
                                variant='outline-primary'
                            >
                                <i aria-hidden='true' className="fa-regular fa-print me-2"></i>
                                <FormattedMessage defaultMessage="Print" id="print.1fd3b13a" />
                            </Button>
                        </div>
                    )}
                    <Row>
                        {component.Results?.SeriesList.map(dataList => dataList.Data.map((item, i) => (
                            <Col className='p-2' key={i} sm={4}>
                                <div className='tile-background rounded bg-theme-item-secondary text-medium d-flex flex-column p-2 h-100'>
                                    <h4 className='fw-bolder fs-5'>
                                        {item.Title}
                                    </h4>
                                    <p className='fw-semibold mb-0 text-strong'>
                                        {item.Share}
                                    </p>
                                </div>
                            </Col>
                        )))}
                    </Row>
                </div>
            );

        }

        if (component.Type === 'Table') {

            const key = `${currentSubReport?.UniqueId}-${component.ComponentId}`;

            return (
                <TableGeneric
                    component={component}
                    index={index}
                    key={key}
                    loading={false}
                    setCurrentSubReport={(report: any) => setState({ currentSubReport: report })}
                />
            );

        }

    };

    const runSubReport = (result: any, index?: number) => {

        if (result.uniqueKey && report60Data) {

            const report = report60Data[result.uniqueKey];

            if (report) {

                setState({ currentSubReport: report });

                return;

            }

        }

        if (index !== undefined && report60Data) {

            const uniqueKey = `${result.ResultId}_${index}`;

            const report = report60Data[uniqueKey];
            setState({ currentSubReport: report });

        }

    };

    const buildTopicAnalysisData = (result: any) => {

        const themes = result.resultPageScoreReportByObjective?.Theme || [];

        return themes.flatMap((theme: any) => {

            const objectives = theme.Objective || [];

            return objectives.map((objective: any) => {

                const r = objective.Results?.[0];

                return {
                    'Pass Percentage': {
                        Color: '#13bed6',
                        Share: String(r?.PassPercentage ?? 0),
                        Type: 'ProgressBar'
                    },
                    Topic: objective.Name
                };

            });

        });

    };

    return (
        <>
            {loading ? (
                <div className='d-flex justify-content-center p-5'>
                    <GMetrixLoader size="large" />
                </div>
            ) : report33Data && currentSubReport ? (
                <>
                    <Container fluid className='mb-3 card card-body'>
                        <Row>

                            <Col md={4} sm={12}>
                                {report33Data && renderListOfActionItem(report33Data.Component)}
                            </Col>
                            <Col md={8} sm={12}>
                                {currentSubReport?.Components?.map((component: any) => (
                                    renderComponents(component, component.ComponentId)
                                ))}
                            </Col>
                        </Row>
                    </Container>
                </>
            ) : (
                <div className='d-flex justify-content-center p-5 fs-5'>
                    <FormattedMessage defaultMessage="There are no results to display." id="there_are_no_results_to_display.bd8a2d3d" />
                </div>
            )}
        </>
    );

};
